import { OpusAgentModel } from "@src/types.generated";

// The models this client knows, in picker order. scadmin deploys ahead of the
// dashboard, so the schema enum can carry members this list lacks: those are
// tolerated at run time (labelled from the enum value, never offered) and
// caught by models.test.ts rather than the type checker, which scadmin's
// dashboard-main schema check runs against the newer enum.
export const OPUS_AGENT_MODEL_ORDER = [
  OpusAgentModel.ClaudeOpus_5_5,
  OpusAgentModel.ClaudeOpus_5,
  OpusAgentModel.ClaudeSonnet_5_5,
  OpusAgentModel.ClaudeSonnet_5,
  OpusAgentModel.ClaudeHaiku_4_5,
  OpusAgentModel.ClaudeFable_5_1,
  OpusAgentModel.Gpt_6Astra,
  OpusAgentModel.Gpt_6Sol,
  OpusAgentModel.Gpt_6Luna,
  OpusAgentModel.Gpt_5_6Sol,
  OpusAgentModel.Gpt_5_6Terra,
  OpusAgentModel.Gpt_5_6Luna,
  OpusAgentModel.DeepseekV4_1Flash,
] as const satisfies readonly OpusAgentModel[];

export type KnownOpusAgentModel = (typeof OPUS_AGENT_MODEL_ORDER)[number];

export const isKnownOpusAgentModel = (
  model: OpusAgentModel,
): model is KnownOpusAgentModel =>
  (OPUS_AGENT_MODEL_ORDER as readonly OpusAgentModel[]).includes(model);

// Human names, keyed by the known set so a newly known member is a type error
// until it gets a name.
export const OPUS_AGENT_MODEL_LABELS: Record<KnownOpusAgentModel, string> = {
  [OpusAgentModel.ClaudeOpus_5_5]: "Claude Opus 5.5",
  [OpusAgentModel.ClaudeOpus_5]: "Claude Opus 5",
  [OpusAgentModel.ClaudeSonnet_5_5]: "Claude Sonnet 5.5",
  [OpusAgentModel.ClaudeSonnet_5]: "Claude Sonnet 5",
  [OpusAgentModel.ClaudeHaiku_4_5]: "Claude Haiku 4.5",
  [OpusAgentModel.ClaudeFable_5_1]: "Claude Fable 5.1",
  [OpusAgentModel.Gpt_6Astra]: "GPT-6 Astra",
  [OpusAgentModel.Gpt_6Sol]: "GPT-6 Sol",
  [OpusAgentModel.Gpt_6Luna]: "GPT-6 Luna",
  [OpusAgentModel.Gpt_5_6Sol]: "GPT-5.6 Sol",
  [OpusAgentModel.Gpt_5_6Terra]: "GPT-5.6 Terra",
  [OpusAgentModel.Gpt_5_6Luna]: "GPT-5.6 Luna",
  [OpusAgentModel.DeepseekV4_1Flash]: "DeepSeek V4.1 Flash",
};

export const opusAgentModelLabel = (model: OpusAgentModel): string =>
  isKnownOpusAgentModel(model)
    ? OPUS_AGENT_MODEL_LABELS[model]
    : String(model).replace(/_/g, " ");

// Product tiers. The menu offers these; they resolve to concrete models
// the send mutation already understands. Swap the mapping here when the
// backend grows its own aliases.
export const OPUS_AGENT_MODEL_PRESETS = {
  auto: OpusAgentModel.ClaudeSonnet_5_5,
  power: OpusAgentModel.ClaudeOpus_5_5,
} as const;

export type OpusAgentModelPreset = keyof typeof OPUS_AGENT_MODEL_PRESETS;

export const OPUS_AGENT_PRESET_ORDER: readonly OpusAgentModelPreset[] = [
  "auto",
  "power",
];

export const OPUS_AGENT_PRESET_LABELS: Record<OpusAgentModelPreset, string> = {
  auto: "Auto",
  power: "Power",
};

export const OPUS_AGENT_PRESET_DESCRIPTIONS: Record<
  OpusAgentModelPreset,
  string
> = {
  auto: "Good default for most questions",
  power: "More capable, takes longer",
};

export const opusAgentPresetForModel = (
  model: OpusAgentModel,
): OpusAgentModelPreset | null => {
  for (const preset of OPUS_AGENT_PRESET_ORDER) {
    if (OPUS_AGENT_MODEL_PRESETS[preset] === model) return preset;
  }
  return null;
};

export const opusAgentPickerLabel = (model: OpusAgentModel): string => {
  const preset = opusAgentPresetForModel(model);
  return preset ? OPUS_AGENT_PRESET_LABELS[preset] : opusAgentModelLabel(model);
};

// Members withheld from the picker. The picker offers only the current
// generation: Opus 5.5, Sonnet 5.5 and GPT-6. Opus 5, Sonnet 5 and Haiku 4.5
// are superseded by the 5.5 tier; Fable is unverified against a real API key
// (30-day-retention requirement, forced-tool_choice sensitivity); the GPT-5.6
// tier is superseded by GPT-6. DeepSeek V4.1 Flash runs only on the Opus
// Harness, and this picker's chat engine refuses it: the Opus Harness
// chat's own picker (OhComposer, behind Ff.OpusHarness) offers it
// instead. Labels and ratings still cover every member: a chat whose stored model is hidden keeps
// showing it — it just isn't offered.
const HIDDEN_OPUS_AGENT_MODELS: ReadonlySet<OpusAgentModel> = new Set([
  OpusAgentModel.ClaudeOpus_5,
  OpusAgentModel.ClaudeSonnet_5,
  OpusAgentModel.ClaudeHaiku_4_5,
  OpusAgentModel.ClaudeFable_5_1,
  OpusAgentModel.Gpt_5_6Sol,
  OpusAgentModel.Gpt_5_6Terra,
  OpusAgentModel.Gpt_5_6Luna,
  OpusAgentModel.DeepseekV4_1Flash,
]);

export const isOpusAgentModelAvailable = (model: OpusAgentModel): boolean =>
  isKnownOpusAgentModel(model) && !HIDDEN_OPUS_AGENT_MODELS.has(model);

// The ordered list the menu renders.
export const OPUS_AGENT_PICKER_MODELS: readonly KnownOpusAgentModel[] =
  OPUS_AGENT_MODEL_ORDER.filter(isOpusAgentModelAvailable);

// Relative speed/intelligence on a 1–5 scale, keyed by the known set so a
// newly known member is a type error until rated. Claude tiers by product
// positioning; the GPT-5.6 tier per its published positioning (Luna
// fast/lighter, Terra balanced, Sol most capable); GPT-6 Astra is the newer-generation flagship
// and outranks Sol and Opus on intelligence.
export type OpusAgentModelRatingValue = 1 | 2 | 3 | 4 | 5;
export const OPUS_AGENT_MODEL_RATING_MAX: OpusAgentModelRatingValue = 5;

export type OpusAgentModelRating = {
  speed: OpusAgentModelRatingValue;
  intelligence: OpusAgentModelRatingValue;
};

export const OPUS_AGENT_MODEL_RATINGS: Record<
  KnownOpusAgentModel,
  OpusAgentModelRating
> = {
  [OpusAgentModel.ClaudeOpus_5_5]: { speed: 1, intelligence: 5 },
  [OpusAgentModel.ClaudeOpus_5]: { speed: 2, intelligence: 4 },
  [OpusAgentModel.ClaudeSonnet_5_5]: { speed: 4, intelligence: 4 },
  [OpusAgentModel.ClaudeSonnet_5]: { speed: 4, intelligence: 3 },
  [OpusAgentModel.ClaudeHaiku_4_5]: { speed: 5, intelligence: 1 },
  [OpusAgentModel.ClaudeFable_5_1]: { speed: 1, intelligence: 5 },
  [OpusAgentModel.Gpt_5_6Sol]: { speed: 2, intelligence: 4 },
  [OpusAgentModel.Gpt_5_6Terra]: { speed: 4, intelligence: 3 },
  [OpusAgentModel.Gpt_5_6Luna]: { speed: 5, intelligence: 1 },
  [OpusAgentModel.Gpt_6Astra]: { speed: 1, intelligence: 5 },
  [OpusAgentModel.Gpt_6Sol]: { speed: 3, intelligence: 4 },
  [OpusAgentModel.Gpt_6Luna]: { speed: 5, intelligence: 2 },
  // A judgment, unmeasured: a flash-class model, below Sonnet 5.5 on
  // intelligence; speed held at the middle until measured. No picker shows
  // it yet; revisit after the first flagged org uses it.
  [OpusAgentModel.DeepseekV4_1Flash]: { speed: 3, intelligence: 3 },
};
